/**
 * entryMotion — SIGNUP-1: the sign-in page's motion, ported from the
 * "OrbitEntry" prototype (Orbit Project tab redesign, Sept 2026).
 *
 * Three pieces, all Web Animations API on plain DOM so React never
 * re-renders mid-animation:
 *
 *   • playEntrance(root)  — photos scale in, the wordmark blurs in,
 *     the form rises line by line.
 *   • playSignInFinale(root, button) — the sign-in finale (below).
 *     Resolves when the page should navigate.
 *   • tileColour(i) — the colour a tile becomes; also used by the
 *     collage to paint each tile's veil.
 *
 * ── ENTRY-80s · THE FINALE, AS A 1980s INTRO ──────────────────────
 *
 * Rennan, Sep 27: "the log in animation with the colors and the
 * images turning into colors, i love it but feels incomplete. it
 * needs to look more retro like a 1980s intro with glow, and more
 * dynamic." The bones he loved are kept — photos become colour, colour
 * becomes lines, lines whip into a sphere, the sphere runs the
 * spectrum, everything blooms white. What was missing was everything
 * a broadcast ident of 1984 would have put around them: light. The
 * old finale had a two-second stretch of one flat disc changing colour
 * on a black void; that was the "incomplete".
 *
 * The beats now, in ms from the press:
 *
 *      0  the form and wordmark blur away; scanlines and a vignette
 *         fade up (a CRT is watching this)
 *    300  the photos flicker on as NEON — each tile lights in its own
 *         colour with a glow, the way a sign warms up; the legibility
 *         scrim lifts so the colours read at full strength
 *    600  the button charges with light, flattens into a rod and
 *         shoots for the centre
 *   1300  the perspective grid floor lights up under the horizon and
 *         starts rushing toward the camera
 *   1500  a starfield warps outward; colour streaks race inward
 *   2300  the sun seeds where the button lands
 *   2800  IMPACT — the rods arrive, a white shockwave ring, a shake,
 *         the sun ignites
 *   3100  the sun runs the spectrum (the halo, not a flat fill) and
 *         throws a rainbow ring outward on every colour, six in all
 *   4400  a rainbow annulus rises behind the centre and turns
 *   4500  the Orbit wordmark flies in from the depth, splitting into
 *         cyan and magenta on the way (chromatic aberration) and
 *         converging into white neon
 *   5350  a light sweep runs across the wordmark
 *   5900  bloom — the annulus rushes past the camera, the wordmark
 *         whites out, the flash takes over
 *   6400  Today
 *
 * Every element is found by a data attribute: [data-tile] + [data-veil]
 * + [data-hot] + [data-scrim] + [data-aurora] (AuthCollageBackground),
 * [data-logo] / [data-m] / [data-blue] (the form), and the finale's own
 * parts in EntryFinaleLayer. `prefers-reduced-motion` skips all of it.
 *
 * ── ENTRY-SMOOTH (Sep 29) · WHY IT ANIMATES ONLY TRANSFORM + OPACITY ──
 *
 * Rennan: "stuttering a lot and then just skips a lot of frames". The
 * finale animated things a browser has to repaint on every frame — box
 * shadows (the button, the sun's colour cycle), filters (a brightness
 * ramp on every tile, blur on the form and the wordmark), shapes inside
 * a full-screen SVG — and it blended three full-screen layers. And the
 * press itself read every tile's position between starting animations,
 * forcing a layout per tile, then lit tiles that were off screen.
 *
 * Now every moving part moves by transform and opacity only, which the
 * compositor runs off the main thread, and anything that glows has its
 * glow painted once, up front: the button's charge is a shadow layer
 * that fades in, the sun's colours are six stacked discs that crossfade,
 * the wordmark's glow is two blurred copies behind it, the tiles heat up
 * under a white layer instead of a brightness filter. Positions are read
 * in one pass before anything starts, only tiles you can see take part,
 * and the wall stops drifting (and drops its aurora blend) the moment
 * the finale begins. The beats and the look are unchanged.
 */

export const ENTRY_PULSE = ['#2F6BE8', '#7FB2E8', '#3FC4B4', '#E8B54A', '#F08A6E', '#B45BC8'];

/** The neon the finale is lit with — Mission Blue and its partners. */
export const NEON = { blue: '#2F6BE8', sky: '#7FB2E8', violet: '#B45BC8', pink: '#E86A7A', teal: '#3FC4B4' };

const TILE_PALETTE = [
  '#E8B54A', '#3FC4B4', '#F08A6E', '#6A5AD0', '#E86A7A', '#7FB2E8', '#B5D334', '#D4A02C',
  '#F29A7E', '#7ECFCF', '#9A5AD0', '#F0C05A', '#C4607A', '#B45BC8', '#2F6BE8',
];

export const tileColour = (i: number) => TILE_PALETTE[i % TILE_PALETTE.length];

export function prefersReducedMotion(): boolean {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

const canAnimate = (root: HTMLElement | null): root is HTMLElement =>
  !!root && typeof root.animate === 'function' && !prefersReducedMotion();

/** Streak geometry in the finale SVG's 1000×1000 space. */
export interface Streak {
  x: number;
  y: number;
  /** The tail end, pointing at the centre. */
  x2: number;
  y2: number;
  c: string;
  w: number;
}

export function makeStreaks(count = 26): Streak[] {
  return Array.from({ length: count }, (_, i) => {
    const ang = (i / count) * Math.PI * 2 + (i % 2) * 0.13;
    const r = 620 + (i % 3) * 140;
    const x = 500 + Math.cos(ang) * r;
    const y = 500 + Math.sin(ang) * r;
    // ENTRY-80s — longer tails than before: the streak reads as a
    // light trail, not a dash.
    const len = 150 + (i % 4) * 60;
    const toC = Math.atan2(500 - y, 500 - x);
    return {
      x: +x.toFixed(1),
      y: +y.toFixed(1),
      x2: +(x + Math.cos(toC) * len).toFixed(1),
      y2: +(y + Math.sin(toC) * len).toFixed(1),
      c: TILE_PALETTE[i % TILE_PALETTE.length],
      w: 3 + (i % 3) * 2,
    };
  });
}

/** One star of the warp field, in the same 1000×1000 space. */
export interface Star {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  w: number;
  o: number;
}

/**
 * ENTRY-80s — the warp starfield: short radial lines that, scaled up
 * as a group from the centre, rush outward and lengthen. Seeded so the
 * field is the same on every render (no hydration mismatch, no jitter
 * between StrictMode passes).
 */
export function makeStars(count = 120): Star[] {
  let seed = 1984;
  const rnd = () => {
    seed = (seed * 1664525 + 1013904223) % 4294967296;
    return seed / 4294967296;
  };
  return Array.from({ length: count }, () => {
    const ang = rnd() * Math.PI * 2;
    const r1 = 40 + rnd() * 560;
    const len = 18 + rnd() * 70;
    return {
      x1: +(500 + Math.cos(ang) * r1).toFixed(1),
      y1: +(500 + Math.sin(ang) * r1).toFixed(1),
      x2: +(500 + Math.cos(ang) * (r1 + len)).toFixed(1),
      y2: +(500 + Math.sin(ang) * (r1 + len)).toFixed(1),
      w: +(1 + rnd() * 2.2).toFixed(2),
      o: +(0.35 + rnd() * 0.65).toFixed(2),
    };
  });
}

const EASE_ENTER = 'cubic-bezier(.16,.84,.24,1)';

/** Is this rect on screen? Tiles off screen (the wall's looping copies, the rows below) aren't worth animating. */
const onScreen = (r: DOMRect) =>
  r.width > 0 && r.bottom > 0 && r.right > 0 && r.top < window.innerHeight && r.left < window.innerWidth;

/** The wall's tiles that can be seen, positions read in one pass (no layout between animations). */
const visibleTiles = (root: HTMLElement) =>
  Array.from(root.querySelectorAll<HTMLElement>('[data-tile]'))
    .map((el) => ({ el, r: el.getBoundingClientRect() }))
    .filter(({ r }) => onScreen(r));

export function playEntrance(root: HTMLElement | null) {
  if (!canAnimate(root)) return;
  requestAnimationFrame(() => {
    visibleTiles(root).forEach(({ el }, i) =>
      el.animate(
        [{ opacity: 0, transform: 'scale(.92) translateY(30px)' }, { opacity: 1, transform: 'none' }],
        { duration: 1100, delay: (i % 7) * 70, easing: EASE_ENTER, fill: 'backwards' },
      ),
    );
    const logo = root.querySelector<HTMLElement>('[data-logo]');
    logo?.animate(
      [
        { opacity: 0, transform: 'scale(.7)', filter: 'blur(14px)' },
        { opacity: 1, transform: 'none', filter: 'blur(0px)' },
      ],
      { duration: 1300, delay: 200, easing: EASE_ENTER, fill: 'backwards' },
    );
    root.querySelectorAll<HTMLElement>('[data-m],[data-blue]').forEach((el, i) =>
      el.animate(
        [
          { opacity: 0, transform: 'translateY(22px)', filter: 'blur(6px)' },
          { opacity: 1, transform: 'none', filter: 'blur(0px)' },
        ],
        { duration: 900, delay: 420 + i * 90, easing: EASE_ENTER, fill: 'backwards' },
      ),
    );
  });
}

/**
 * ENTRY-CUT-1 — the finale ends at the hyperspace.
 *
 * Rennan: "the sign in animation needs to do just the colorful image to
 * hyperspace animation, but kill the rest of the sequence." So the
 * beats that survive are the ones he named: the photos flicker on as
 * neon, the button charges and shoots for the centre, the grid floor
 * lights and rushes, and the starfield warps. Everything from the sun
 * seeding onward — the impact, the spectrum, six rainbow rings, the
 * annulus, the wordmark flying in with its light sweep, the white
 * bloom — does not run.
 *
 * It is CUT, not deleted. `schedule` refuses anything at or past
 * `CUT_MS`, so the choreography below is intact and unreachable: the
 * 1980s finale was a lot of work and it took three rounds to get the
 * timing right, which makes it worth keeping legible for whoever wants
 * a piece of it back. Nothing past the cut is dead code by accident —
 * one constant is the whole switch.
 */
const CUT_MS = 2200;

/** Total length of the finale before Today should appear. */
export const FINALE_MS = CUT_MS;

/** The finale's beats, ms from the press. See the file header. */
const T = {
  neon: 300,
  charge: 600,
  grid: 1300,
  stars: 1500,
  seed: 2300,
  impact: 2800,
  rings: 3100,
  ringGap: 360,
  annulus: 4400,
  logo: 4500,
  sweep: 5350,
  bloom: 5900,
} as const;

const OUT = 'cubic-bezier(.5,0,.3,1)';
const SNAP = 'cubic-bezier(.2,.7,.2,1)';
const C = 'translate(-50%,-50%)';

/**
 * ENTRY-SMOOTH — start a part of the finale shortly before its beat.
 * Creating every animation on the press made the browser draw every
 * layer at once (the grid, both starfields, the sun's six discs, the
 * wordmark, a glow for every tile) and that one long frame was the
 * stutter at the start. Each part now gets LEAD ms of warning to be
 * drawn, and the drawing is spread over the whole finale.
 */
const LEAD = 160;

/// ENTRY-OPTIMISTIC-1 — every timer the running finale is holding, so
/// `cancelSignInFinale` can clear them. Reset at the start of each run.
let timers: number[] = [];
/// The teardown for the run that is up, if one is.
let teardown: (() => void) | null = null;

function schedule(start: number, run: (delay: number) => void) {
  // ENTRY-CUT-1 — the one switch. Anything the old finale scheduled at
  // or past the cut simply never starts, so the parts that do run are
  // byte-for-byte what they were.
  if (start >= CUT_MS) return;
  if (start <= LEAD) run(start);
  else timers.push(window.setTimeout(() => run(LEAD), start - LEAD));
}

/**
 * ENTRY-OPTIMISTIC-1 — stand the finale down and put the screen back.
 *
 * The finale now starts on the press, ahead of the sign-in's answer, so
 * a failure has to leave no trace: the promise `playSignInFinale`
 * returned simply never resolves, so nothing navigates.
 *
 * Unlike iOS — where the layer is a view and unmounting it cancels
 * everything — this one works on the real DOM, so it has to put back
 * exactly what it touched: its timers, the node it added to the button,
 * the styles it set, and its animations. The wall's own columns were
 * only PAUSED, so they are played rather than cancelled; cancelling
 * them would reset the scroll they were drifting through.
 */
export function cancelSignInFinale() {
  const run = teardown;
  teardown = null;
  run?.();
}

export function playSignInFinale(root: HTMLElement | null, button: HTMLElement | null): Promise<void> {
  if (!canAnimate(root) || !button) return Promise.resolve();

  // ENTRY-OPTIMISTIC-1 — anything still up from a previous run goes
  // first, then this run starts collecting what it will have to undo.
  cancelSignInFinale();
  timers = [];
  const room = root as HTMLElement;
  const made: HTMLElement[] = [];
  const held: Animation[] = [];
  const restore: Array<() => void> = [];
  let live = true;

  teardown = () => {
    live = false;
    timers.forEach((id) => window.clearTimeout(id));
    timers = [];
    // Ours go; the wall's columns were only paused, so they carry on.
    room.getAnimations({ subtree: true }).forEach((a) => {
      if (held.includes(a)) a.play();
      else a.cancel();
    });
    made.forEach((el) => el.remove());
    restore.forEach((put) => put());
  };

  const rr = root.getBoundingClientRect();
  const cx = rr.width / 2;
  // The centre the finale converges on. EntryFinaleLayer puts every
  // centred part at `top: min(50%, 450px)` to match.
  const cy = Math.min(rr.height, 900) / 2;
  const br = button.getBoundingClientRect();
  const bx = br.left + br.width / 2 - rr.left;
  const by = br.top + br.height / 2 - rr.top;

  const one = <E extends Element>(sel: string) => root.querySelector<E>(sel);
  const all = <E extends Element>(sel: string) => Array.from(root.querySelectorAll<E>(sel));

  // Every position the finale needs, read now in one pass — before any
  // animation starts, so reading doesn't force a layout per tile.
  const tiles = all<HTMLElement>('[data-tile]').map((el) => ({ el, r: el.getBoundingClientRect() }));
  const stage = one<HTMLElement>('[data-finale]');
  const sw = stage?.clientWidth || rr.width;
  const sh = stage?.clientHeight || rr.height;

  // Freeze the wall where it is: the columns stop drifting (so every
  // tile flies from where it really is), and the aurora — a screen blend
  // over the whole wall — fades out rather than being re-blended under
  // the neon on every frame.
  all<HTMLElement>('.auth-col-up,.auth-col-down').forEach((col) =>
    col.getAnimations().forEach((a) => {
      a.pause();
      held.push(a);
    }),
  );
  const aurora = one<HTMLElement>('[data-aurora]');
  if (aurora) restore.push(() => { aurora.style.display = ''; });
  aurora
    ?.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 400, easing: 'ease-out', fill: 'forwards' })
    .finished.then(() => {
      // Only if this run is still the one that is up: a cancelled
      // finale must not hide the wall's aurora on its way out.
      if (live) aurora.style.display = 'none';
    })
    .catch(() => {});

  // ── 0 · the form and wordmark fall away; the CRT comes on ───────
  // (Opacity and a small drift, not a blur: a blur filter re-renders
  // every frame.)
  all<HTMLElement>('[data-m],[data-logo]').forEach((el, i) =>
    el.animate(
      [
        { opacity: 1, transform: 'none' },
        { opacity: 0, transform: 'translateY(-10px) scale(.97)' },
      ],
      { duration: 450, delay: i * 30, easing: 'ease-in', fill: 'forwards' },
    ),
  );
  one<HTMLElement>('[data-scan]')?.animate(
    [
      { opacity: 0, offset: 0 },
      { opacity: 1, offset: 0.12 },
      { opacity: 1, offset: 0.9 },
      { opacity: 0, offset: 1 },
    ],
    { duration: FINALE_MS, easing: 'ease-in-out', fill: 'forwards' },
  );
  // The legibility scrim lifts so the neon reads at full strength.
  one<HTMLElement>('[data-scrim]')?.animate([{ opacity: 1 }, { opacity: 0 }], {
    duration: 900,
    delay: T.neon,
    easing: 'ease-in-out',
    fill: 'forwards',
  });

  // ── the button charges, flattens into a rod, shoots for the centre ──
  // The charge is a glow layer inside the button whose OPACITY rises —
  // painted once — not an animated box-shadow, which repainted every
  // frame. The idle breathing glow and shimmer stop, and the button lets
  // its glow spill (its overflow clip would cut it).
  const buttonAnimation = button.style.animation;
  const buttonOverflow = button.style.overflow;
  restore.push(() => {
    button.style.animation = buttonAnimation;
    button.style.overflow = buttonOverflow;
  });
  button.style.animation = 'none';
  button.style.overflow = 'visible';
  button.querySelectorAll<HTMLElement>('.entry-shimmer').forEach((el) => {
    const was = el.style.display;
    restore.push(() => { el.style.display = was; });
    el.style.display = 'none';
  });
  const buttonMs = T.seed - T.charge + 200;
  button.animate(
    [
      { transform: 'none', opacity: 1, offset: 0 },
      { transform: 'none', opacity: 1, offset: 0.26 },
      { transform: 'scaleY(.08)', opacity: 1, offset: 0.5 },
      { transform: `translate(${cx - bx}px,${cy - by}px) scaleY(.05) scaleX(.1)`, opacity: 0, offset: 1 },
    ],
    { duration: buttonMs, delay: T.charge, easing: OUT, fill: 'forwards' },
  );
  schedule(T.charge, (delay) => {
    const charge = document.createElement('span');
    charge.setAttribute('aria-hidden', 'true');
    Object.assign(charge.style, {
      position: 'absolute',
      inset: '0',
      borderRadius: 'inherit',
      pointerEvents: 'none',
      opacity: '0',
      boxShadow: `0 0 18px 6px ${NEON.sky}, 0 0 28px 4px ${NEON.blue}, 0 0 90px 22px rgba(47,107,232,.7)`,
    });
    button.prepend(charge);
    made.push(charge);
    charge.animate(
      [
        { opacity: 0, offset: 0 },
        { opacity: 1, offset: 0.26 },
        { opacity: 1, offset: 0.85 },
        { opacity: 0, offset: 1 },
      ],
      { duration: buttonMs, delay, easing: OUT, fill: 'forwards' },
    );
  });

  // ── 1 · photos flicker on as neon, 2 · squeeze into rods, 3 · whip in ──
  tiles.forEach(({ el, r }) => {
    // Off screen: nothing to see, nothing to animate — it just goes.
    if (!onScreen(r)) {
      el.style.visibility = 'hidden';
      return;
    }
    const veil = el.querySelector<HTMLElement>('[data-veil]');
    const hot = el.querySelector<HTMLElement>('[data-hot]');
    const tx = r.left + r.width / 2 - rr.left;
    const ty = r.top + r.height / 2 - rr.top;
    const d = Math.hypot(tx - cx, ty - cy);
    const dl = Math.min(d / 3, 500);
    const rot = (Math.atan2(cy - ty, cx - tx) * 180) / Math.PI;
    schedule(T.neon + dl, (delay) => {
      // A sign warming up: two false starts, then steady. The veil
      // carries the tile's glow (a static box-shadow), so the glow
      // flickers on with it at no extra paint.
      veil?.animate(
        [
          { opacity: 0, offset: 0 },
          { opacity: 0.9, offset: 0.16 },
          { opacity: 0.12, offset: 0.24 },
          { opacity: 1, offset: 0.34 },
          { opacity: 0.45, offset: 0.44 },
          { opacity: 1, offset: 0.58 },
          { opacity: 1, offset: 1 },
        ],
        { duration: 800, delay, easing: 'linear', fill: 'forwards' },
      );
      const flight: KeyframeAnimationOptions = { duration: 2000, delay, easing: OUT, fill: 'forwards' };
      // The rod runs white-hot as it squeezes and flies: a white layer
      // over the neon, fading up (it was a brightness filter on the
      // whole tile — re-rendered every frame, for every tile).
      hot?.animate(
        [
          { opacity: 0, offset: 0 },
          { opacity: 0, offset: 0.34 },
          { opacity: 0.35, offset: 0.55 },
          { opacity: 0.6, offset: 0.8 },
          { opacity: 0.8, offset: 1 },
        ],
        flight,
      );
      // Once a tile has gone, pin it gone. A `fill: forwards` end state
      // can be dropped by the compositor when a lazy image decodes
      // late; writing the final opacity inline can't be.
      const gone = el.animate(
        [
          { transform: 'none', opacity: 1, offset: 0 },
          { transform: 'none', opacity: 1, offset: 0.34 },
          { transform: 'scaleY(.06) scaleX(1.15)', opacity: 1, offset: 0.55 },
          {
            transform: `translate(${(cx - tx) * 0.5}px,${(cy - ty) * 0.5}px) rotate(${rot}deg) scaleY(.03) scaleX(1.7)`,
            opacity: 1,
            offset: 0.8,
          },
          {
            transform: `translate(${cx - tx}px,${cy - ty}px) rotate(${rot}deg) scaleY(.02) scaleX(.05)`,
            opacity: 0,
            offset: 1,
          },
        ],
        flight,
      );
      gone.finished
        .then(() => {
          el.style.opacity = '0';
          el.style.visibility = 'hidden';
        })
        .catch(() => {});
    });
  });

  // ── the grid floor lights and rushes toward the camera ──────────
  const gridFade: Keyframe[] = [
    { opacity: 0, offset: 0 },
    { opacity: 1, offset: 0.16 },
    { opacity: 1, offset: 0.86 },
    { opacity: 0, offset: 1 },
  ];
  const gridMs = T.bloom + 300 - T.grid;
  schedule(T.grid, (delay) => {
    one<HTMLElement>('[data-grid]')?.animate(gridFade, { duration: gridMs, delay, easing: 'ease-in-out', fill: 'forwards' });
    one<HTMLElement>('[data-horizon]')?.animate(gridFade, { duration: gridMs, delay, easing: 'ease-in-out', fill: 'forwards' });
    // One line-spacing per loop (64px in the floor's own space), so the
    // loop seam is invisible. Faster after the impact.
    const lines = one<HTMLElement>('[data-grid-lines]');
    lines?.animate([{ transform: 'translateY(0px)' }, { transform: 'translateY(64px)' }], {
      duration: 640,
      delay,
      iterations: (T.impact - T.grid) / 640,
      easing: 'linear',
    });
    lines?.animate([{ transform: 'translateY(0px)' }, { transform: 'translateY(64px)' }], {
      duration: 300,
      delay: delay + T.impact - T.grid,
      iterations: (gridMs + T.grid - T.impact) / 300,
      easing: 'linear',
    });
  });

  // ── the starfield warps outward, twice ──────────────────────────
  // (Each pass is an HTML layer holding its own SVG, so the scale runs
  // on the compositor; scaling a group inside an SVG redrew it.)
  all<HTMLElement>('[data-stars]').forEach((g, i) =>
    schedule(i === 0 ? T.stars : T.impact + 200, (delay) =>
      g.animate(
        [
          { opacity: 0, transform: 'scale(.12)', offset: 0 },
          { opacity: 1, offset: 0.22 },
          { opacity: 0.9, transform: 'scale(1.5)', offset: 0.86 },
          { opacity: 0, transform: 'scale(1.8)', offset: 1 },
        ],
        { duration: i === 0 ? 1700 : 2600, delay, easing: 'cubic-bezier(.4,0,.6,1)', fill: 'forwards' },
      ),
    ),
  );

  // ── colour streaks race into the centre with the rods ───────────
  // Each trail is an HTML rod: its length, angle and place are set
  // here, in stage pixels (the 1000×1000 space stretched to the stage),
  // and it travels by transform.
  schedule(T.stars, (delay) =>
    all<HTMLElement>('[data-streak]').forEach((ln, i) => {
      const d = ln.dataset;
      const x = (+(d.x ?? 0) / 1000) * sw;
      const y = (+(d.y ?? 0) / 1000) * sh;
      const x2 = (+(d.x2 ?? 0) / 1000) * sw;
      const y2 = (+(d.y2 ?? 0) / 1000) * sh;
      const ang = (Math.atan2(y2 - y, x2 - x) * 180) / Math.PI;
      const dx = sw / 2 - x;
      const dy = sh / 2 - y;
      ln.style.width = `${Math.hypot(x2 - x, y2 - y)}px`;
      ln.style.left = `${x}px`;
      ln.style.top = `${y}px`;
      ln.animate(
        [
          { opacity: 0, transform: `translateY(-50%) rotate(${ang}deg)`, offset: 0 },
          { opacity: 1, offset: 0.1 },
          { opacity: 1, transform: `translate(${dx * 0.92}px,${dy * 0.92}px) translateY(-50%) rotate(${ang}deg)`, offset: 0.9 },
          { opacity: 0, transform: `translate(${dx}px,${dy}px) translateY(-50%) rotate(${ang}deg)`, offset: 1 },
        ],
        { duration: 1000, delay: delay + (i % 6) * 80, easing: 'cubic-bezier(.6,0,.4,1)', fill: 'forwards' },
      );
    }),
  );

  // ── IMPACT: shockwave, shake, ignition ──────────────────────────
  schedule(T.impact - 20, (delay) => {
    one<HTMLElement>('[data-shock]')?.animate(
      [
        { opacity: 0, transform: `${C} scale(.15)`, offset: 0 },
        { opacity: 1, offset: 0.08 },
        { opacity: 0, transform: `${C} scale(3.4)`, offset: 1 },
      ],
      { duration: 900, delay, easing: SNAP, fill: 'forwards' },
    );
    root.animate(
      [
        { transform: 'none' },
        { transform: 'translate(7px,-4px)' },
        { transform: 'translate(-6px,4px)' },
        { transform: 'translate(4px,2px)' },
        { transform: 'translate(-2px,-1px)' },
        { transform: 'none' },
      ],
      { duration: 320, delay: delay + 10, easing: 'linear' },
    );
  });

  // ── the sun: seeded by the rod, ignited at impact, then the spectrum ──
  const orb = one<HTMLElement>('[data-orb]');
  if (orb) {
    const orbMs = FINALE_MS - T.seed;
    const at = (ms: number) => (ms - T.seed) / orbMs;
    const pk: Keyframe[] = [
      { opacity: 0, transform: `${C} scale(.15)`, offset: 0 },
      { opacity: 0.9, transform: `${C} scale(.45)`, offset: at(T.impact) },
      { opacity: 1, transform: `${C} scale(1.3)`, offset: at(T.impact + 160) },
      { opacity: 1, transform: `${C} scale(1)`, offset: at(T.rings) },
    ];
    // A pulse on every ring.
    ENTRY_PULSE.forEach((_, i) => {
      const t = T.rings + i * T.ringGap;
      pk.push(
        { transform: `${C} scale(1.14)`, offset: at(t + 60) },
        { transform: `${C} scale(1)`, offset: at(t + 300) },
      );
    });
    pk.push(
      { opacity: 1, transform: `${C} scale(1)`, offset: at(T.bloom) },
      { opacity: 0, transform: `${C} scale(.55)`, offset: 1 },
    );
    schedule(T.seed, (delay) => orb.animate(pk, { duration: orbMs, delay, easing: 'ease-in-out', fill: 'forwards' }));
    // The spectrum: disc and halo run the six colours, one per ring,
    // while the white core on top stays white. Each colour is its own
    // disc with its halo painted once (EntryFinaleLayer); the next one
    // fades in over the last. (Animating the colour and the shadow
    // repainted a 170px glow every frame; a hue-rotate filter would
    // clip the glow to a square.)
    // Only two discs are ever drawn: each one leaves once the next has
    // fully covered it.
    const last = ENTRY_PULSE.length - 1;
    const step = (T.bloom - T.rings) / last;
    orb.querySelectorAll<HTMLElement>('[data-orb-colour]').forEach((disc) => {
      const i = Number(disc.dataset.orbColour);
      const leaves = i < last;
      const frames: Keyframe[] =
        i === 0
          ? [{ opacity: 1 }, { opacity: 1, offset: 0.999 }, { opacity: 0 }]
          : leaves
            ? [{ opacity: 0 }, { opacity: 1, offset: 0.5 }, { opacity: 1, offset: 0.999 }, { opacity: 0 }]
            : [{ opacity: 0 }, { opacity: 1 }];
      const from = i === 0 ? T.rings : T.rings + (i - 1) * step;
      const ms = i === 0 ? step : leaves ? 2 * step : step;
      schedule(from, (delay) => disc.animate(frames, { duration: ms, delay, easing: 'linear', fill: 'forwards' }));
    });
  }

  // ── six rainbow rings, one per colour ───────────────────────────
  all<HTMLElement>('[data-ring]').forEach((el, i) =>
    schedule(T.rings + i * T.ringGap, (delay) =>
      el.animate(
        [
          { opacity: 0, transform: `${C} scale(.2)`, offset: 0 },
          { opacity: 0.95, offset: 0.1 },
          { opacity: 0, transform: `${C} scale(2.9)`, offset: 1 },
        ],
        { duration: 1300, delay, easing: 'cubic-bezier(.2,.6,.3,1)', fill: 'forwards' },
      ),
    ),
  );

  // ── the rainbow annulus rises behind the centre, then rushes past ──
  schedule(T.annulus, (delay) =>
    one<HTMLElement>('[data-core]')?.animate(
      [
        { opacity: 0, transform: `${C} scale(.2) rotate(0deg)`, offset: 0 },
        { opacity: 0.85, transform: `${C} scale(1.1) rotate(120deg)`, offset: 0.4 },
        { opacity: 0.9, transform: `${C} scale(1.6) rotate(220deg)`, offset: 0.72 },
        { opacity: 1, transform: `${C} scale(9) rotate(330deg)`, offset: 0.95 },
        { opacity: 0, transform: `${C} scale(11) rotate(360deg)`, offset: 1 },
      ],
      { duration: FINALE_MS - T.annulus, delay, easing: OUT, fill: 'forwards' },
    ),
  );

  // ── the wordmark flies in from the depth, splits, converges, sweeps ──
  // (Scale and opacity only — the blur-in and the brightness blow-out
  // re-filtered the whole glowing, masked wordmark every frame. It
  // still flies in from depth; the bloom flash whites it out.)
  const logoMs = FINALE_MS - T.logo;
  const lat = (ms: number) => (ms - T.logo) / logoMs;
  schedule(T.logo, (delay) =>
    one<HTMLElement>('[data-logo-wrap]')?.animate(
      [
        { opacity: 0, transform: `${C} scale(3)`, offset: 0 },
        { opacity: 1, transform: `${C} scale(1)`, offset: lat(T.sweep - 50) },
        { opacity: 1, transform: `${C} scale(1)`, offset: lat(T.bloom) },
        { opacity: 1, transform: `${C} scale(1.3)`, offset: 1 },
      ],
      { duration: logoMs, delay, easing: SNAP, fill: 'forwards' },
    ),
  );
  const split = (from: number): Keyframe[] => [
    { opacity: 0, transform: `translateX(${from}px)`, offset: 0 },
    { opacity: 0.9, offset: 0.3 },
    { opacity: 0.9, transform: `translateX(${from / 6}px)`, offset: 0.8 },
    { opacity: 0, transform: 'translateX(0px)', offset: 1 },
  ];
  schedule(T.logo + 200, (delay) => {
    one<HTMLElement>('[data-logo-c]')?.animate(split(-44), { duration: 900, delay, easing: SNAP, fill: 'forwards' });
    one<HTMLElement>('[data-logo-m]')?.animate(split(44), { duration: 900, delay, easing: SNAP, fill: 'forwards' });
  });
  schedule(T.sweep, (delay) =>
    one<HTMLElement>('[data-sweep]')?.animate([{ transform: 'translateX(-120%)' }, { transform: 'translateX(120%)' }], {
      duration: 700,
      delay,
      easing: 'cubic-bezier(.4,0,.2,1)',
      fill: 'forwards',
    }),
  );

  // ── bloom to white; Today is under it ───────────────────────────
  schedule(T.bloom - 100, (delay) =>
    one<HTMLElement>('[data-flash]')?.animate(
      [
        { opacity: 0, offset: 0 },
        { opacity: 0.15, offset: 0.4 },
        { opacity: 1, offset: 1 },
      ],
      { duration: FINALE_MS - T.bloom + 100, delay, easing: 'ease-in', fill: 'forwards' },
    ),
  );

  // A cancelled run clears this with the rest, so the promise never
  // settles and nothing downstream navigates.
  //
  // ENTRY-RETURN-1b — but the teardown STAYS after the run settles. It
  // used to be dropped here, which quietly meant a sign-in slower than
  // the sequence could not be undone: `cancelSignInFinale()` found
  // nothing to run, the form's fade is `fill: 'forwards'`, and the page
  // sat on the wordmark with an error message it had faded to nothing.
  // 6.4s is not a long time to wait for a bad network.
  //
  // Keeping it costs a few detached-node references until the next run
  // (every `playSignInFinale` tears down the previous one first), and
  // running it late is inert: no animations are left to cancel, and the
  // restores put back styles on elements React has usually dropped.
  return new Promise((resolve) => {
    timers.push(window.setTimeout(resolve, FINALE_MS));
  });
}
